<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="handleCancel"></div>
    
    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col transform transition-all">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 rounded-t-2xl">
          <div class="flex items-center">
            <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900">End User License Terms</h3>
          </div>
          <button 
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="loading" class="flex items-center justify-center py-12">
            <svg class="animate-spin h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          
          <div v-else-if="error" class="text-center py-12">
            <svg class="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p class="text-gray-600">{{ error }}</p>
            <button @click="loadEula" class="mt-4 text-purple-600 hover:text-purple-700 font-medium">
              Try Again
            </button>
          </div>
          
          <div v-else class="prose prose-sm max-w-none" v-html="eulaContent"></div>
        </div>
        
        <!-- Footer with buttons -->
        <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div class="flex gap-3">
            <button
              @click="handleCancel"
              class="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              @click="handleAccept"
              :disabled="loading || !!error"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'accept'])

const eulaContent = ref('')
const loading = ref(false)
const error = ref(null)

const loadEula = async () => {
  loading.value = true
  error.value = null
  
  try {
    const response = await fetch('/eula.html')
    if (!response.ok) {
      throw new Error('Failed to load EULA')
    }
    eulaContent.value = await response.text()
  } catch (err) {
    console.error('Failed to load EULA:', err)
    error.value = 'Failed to load End User License Terms. Please try again.'
  } finally {
    loading.value = false
  }
}

const handleAccept = () => {
  emit('accept')
  emit('close')
}

const handleCancel = () => {
  emit('close')
}

// Load EULA when modal opens
watch(() => props.show, (newVal) => {
  if (newVal && !eulaContent.value) {
    loadEula()
  }
})
</script>

<style scoped>
.prose {
  color: #374151;
  line-height: 1.6;
}

.prose :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #111827;
}

.prose :deep(h2) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: #1f2937;
}

.prose :deep(h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: #374151;
}

.prose :deep(p) {
  margin-bottom: 0.75rem;
}

.prose :deep(ul), .prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 0.75rem;
}

.prose :deep(li) {
  margin-bottom: 0.25rem;
}

.prose :deep(a) {
  color: #7c3aed;
  text-decoration: underline;
}

.prose :deep(strong) {
  font-weight: 600;
}
</style>
